import { useEffect, useMemo, useState } from "react";
import { RecipeListItem, MealType, WeekSlotDto, IngredientDetail, api } from "../api/client";
import { SLOT_ALLOWED_TYPES, SLOT_ROWS } from "../constants";
import { foldText } from "../utils/text";
import { Thumb } from "./Thumb";

interface Props {
  slot: WeekSlotDto;
  recipes: RecipeListItem[];
  onPick: (recipeId: number) => void;
  onClose: () => void;
}

type Tab = "recipes" | "ingredients";

// Tint del punt segons el tipus de recepta (els subrecipe queden exclosos del picker).
const TINT_BY_TYPE: Record<MealType, string> = {
  breakfast: "mc-breakfast",
  lunch: "mc-lunch",
  snack: "mc-snack",
  subrecipe: "mc-snack",
};

/**
 * Modal per triar una recepta i afegir-la a un slot. Substitueix l'arrossegar
 * de la biblioteca a mòbil i és l'única manera d'afegir al tauler redissenyat.
 */
export function RecipePickerSheet({ slot, recipes, onPick, onClose }: Props) {
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState<Tab>("recipes");
  const [ingredients, setIngredients] = useState<IngredientDetail[]>([]);
  const [ingLoading, setIngLoading] = useState(false);
  const [pendingIng, setPendingIng] = useState<number | null>(null);

  // Tanca amb Escape i bloqueja l'scroll del fons mentre està obert.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Carrega ingredients (amb cerca) només quan la pestanya és activa.
  useEffect(() => {
    if (tab !== "ingredients") return;
    let cancelled = false;
    setIngLoading(true);
    const q = filter.trim();
    const params = new URLSearchParams({ limit: "40", canBeMeal: "true" });
    if (q) params.set("q", q);
    const t = window.setTimeout(() => {
      api
        .get<IngredientDetail[]>(`/ingredients?${params.toString()}`)
        .then((data) => {
          if (!cancelled) setIngredients(Array.isArray(data) ? data : []);
        })
        .catch(() => {
          if (!cancelled) setIngredients([]);
        })
        .finally(() => {
          if (!cancelled) setIngLoading(false);
        });
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [tab, filter]);

  const slotLabel = SLOT_ROWS.find((r) => r.slot === slot.mealSlot)?.label ?? "";

  const list = useMemo(() => {
    const allowed = SLOT_ALLOWED_TYPES[slot.mealSlot];
    const q = foldText(filter.trim());
    return recipes
      .filter((r) => allowed.includes(r.mealType))
      .filter((r) => (q ? foldText(r.name).includes(q) : true));
  }, [recipes, slot.mealSlot, filter]);

  async function pickIngredient(ingredientId: number) {
    if (pendingIng != null) return;
    setPendingIng(ingredientId);
    try {
      const recipe = await api.post<RecipeListItem>(`/recipes/from-ingredient/${ingredientId}`);
      onPick(recipe.id);
    } catch {
      setPendingIng(null);
    }
  }

  return (
    <div className="picker-scrim" onClick={onClose}>
      <div
        className="picker-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Afegeix recepta per ${slotLabel}`}
      >
        <div className="picker-hd">
          <div>
            <div className="text-base font-extrabold">Afegeix al menú</div>
            <div className="text-[13px] font-semibold text-muted">{slotLabel}</div>
          </div>
          <button type="button" className="picker-close" aria-label="Tanca" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="picker-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "recipes"}
            className={`picker-tab ${tab === "recipes" ? "active" : ""}`}
            onClick={() => setTab("recipes")}
          >
            Receptes
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ingredients"}
            className={`picker-tab ${tab === "ingredients" ? "active" : ""}`}
            onClick={() => setTab("ingredients")}
          >
            Ingredients
          </button>
        </div>

        <div className="picker-search">
          <span className="picker-search-ic">
            <SearchIcon />
          </span>
          <input
            autoFocus
            placeholder={tab === "recipes" ? "Cerca una recepta…" : "Cerca un ingredient…"}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {tab === "recipes" ? (
          <div className="picker-list">
            {list.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">
                {filter ? "Cap recepta trobada" : "Cap recepta compatible amb aquesta franja."}
              </div>
            ) : (
              list.map((r) => (
                <button key={r.id} type="button" className="picker-row" onClick={() => onPick(r.id)}>
                  <span className={`picker-dot ${TINT_BY_TYPE[r.mealType]}`} />
                  <span className="picker-name">{r.name}</span>
                  <span className="picker-plus">
                    <PlusIcon />
                  </span>
                </button>
              ))
            )}
          </div>
        ) : (
          <div className="picker-list">
            {ingLoading && ingredients.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">Carregant…</div>
            ) : ingredients.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">
                {filter ? "Cap ingredient trobat" : "Escriu per cercar un ingredient."}
              </div>
            ) : (
              ingredients.map((ing) => (
                <button
                  key={ing.id}
                  type="button"
                  className="picker-row"
                  disabled={pendingIng != null}
                  onClick={() => void pickIngredient(ing.id)}
                >
                  <Thumb name={ing.name} imageUrl={ing.imageUrl} meal="snack" className="picker-ing-thumb" size={16} />
                  <span className="picker-name">{ing.name}</span>
                  <span className="picker-plus">
                    <PlusIcon />
                  </span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <style>{`
        /* Ancorat a dalt (no centrat): així el modal no es mou quan la cerca canvia l'alçada de la llista */
        .picker-scrim{ position:fixed; inset:0; z-index:60; background:rgba(25,18,12,.42); display:flex; justify-content:center; align-items:flex-start; padding:20px; padding-top:max(20px, 10vh); animation:fade-in .15s ease; }
        .picker-modal{ width:440px; max-width:100%; max-height:80vh; background:var(--background); border:1px solid var(--border); border-radius:18px; box-shadow:var(--shadow-card-lg); padding:18px; display:flex; flex-direction:column; animation:pop .18s ease; }
        .picker-hd{ display:flex; align-items:flex-start; justify-content:space-between; gap:12px; margin-bottom:14px; }
        .picker-close{ width:38px; height:38px; border-radius:11px; border:1px solid var(--border-strong); background:var(--surface); color:var(--foreground); display:grid; place-items:center; cursor:pointer; }
        .picker-close:hover{ background:var(--surface-secondary); }
        .picker-tabs{ display:flex; gap:4px; padding:3px; margin-bottom:12px; background:var(--surface-secondary); border-radius:12px; }
        .picker-tab{ flex:1; border:0; background:transparent; cursor:pointer; font:inherit; font-weight:700; font-size:13px; color:var(--muted); padding:8px 10px; border-radius:9px; transition:background .12s,color .12s; }
        .picker-tab.active{ background:var(--background); color:var(--foreground); box-shadow:var(--shadow-card-sm); }
        .picker-ing-thumb{ width:26px; height:26px; border-radius:8px; flex:0 0 26px; }
        .picker-row:disabled{ opacity:.5; cursor:default; }
        .picker-search{ position:relative; margin-bottom:12px; }
        .picker-search input{ width:100%; border:1px solid var(--border-strong); background:var(--surface); border-radius:13px; padding:12px 14px 12px 42px; font:inherit; font-weight:600; color:var(--foreground); box-shadow:var(--shadow-card-sm); }
        .picker-search input::placeholder{ color:var(--faint); font-weight:600; }
        .picker-search input:focus{ outline:none; border-color:var(--accent); box-shadow:0 0 0 3px var(--accent-soft); }
        .picker-search-ic{ position:absolute; left:14px; top:50%; transform:translateY(-50%); color:var(--faint); display:grid; place-items:center; }
        .picker-list{ overflow-y:auto; display:flex; flex-direction:column; gap:4px; }
        .picker-row{ display:flex; align-items:center; gap:11px; border:0; background:transparent; cursor:pointer; font:inherit; text-align:left; padding:7px 8px; border-radius:11px; }
        .picker-row:hover{ background:var(--surface-secondary); }
        .picker-dot{ width:11px; height:11px; border-radius:50%; background:var(--mc,var(--border-strong)); flex:0 0 11px; }
        .picker-name{ flex:1; font-weight:700; font-size:14px; }
        .picker-plus{ width:28px; height:28px; border-radius:8px; display:grid; place-items:center; background:var(--accent-soft); color:var(--accent-soft-foreground); flex:0 0 28px; }
      `}</style>
    </div>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.5" y2="16.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}
